import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';
import { SemanticModelLogicalTable, SemanticModelRelationship } from '../../../../../../../api/initiative-data-integrations';
import { BasicAlert } from '@g17eco/molecules/alert';

// Relationship Configuration Form Component
interface RelationshipConfigFormProps {
  relationship: SemanticModelRelationship;
  tables: SemanticModelLogicalTable[];
  onRelationshipChange: (relationship: SemanticModelRelationship) => void;
}

export const RelationshipConfigForm: React.FC<RelationshipConfigFormProps> = ({
  relationship,
  tables,
  onRelationshipChange,
}) => {
  const handleRelationshipChange = (field: keyof SemanticModelRelationship, value: any) => {
    onRelationshipChange({ ...relationship, [field]: value });
  };

  const addColumnMapping = () => {
    const newMapping = { left_column: '', right_column: '' };
    onRelationshipChange({ ...relationship, relationship_columns: [...relationship.relationship_columns, newMapping] });
  };

  const updateColumnMapping = (index: number, field: 'left_column' | 'right_column', value: string) => {
    const updatedColumns = [...relationship.relationship_columns];
    updatedColumns[index] = { ...updatedColumns[index], [field]: value };
    onRelationshipChange({ ...relationship, relationship_columns: updatedColumns });
  };

  const removeColumnMapping = (index: number) => {
    const updatedColumns = [...relationship.relationship_columns];
    updatedColumns.splice(index, 1);
    onRelationshipChange({ ...relationship, relationship_columns: updatedColumns });
  };

  return (
    <div>
      {/* Basic Relationship Info */}
      <Row className='mb-3'>
        <Col lg={6}>
          <FormGroup>
            <Label>Relationship Name *</Label>
            <Input
              type='text'
              value={relationship.name}
              onChange={(e) => handleRelationshipChange('name', e.target.value)}
              placeholder='e.g., orders_to_customers'
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Table Selection */}
      <Row className='mb-3'>
        <Col lg={6}>
          <FormGroup>
            <Label>Left Table</Label>
            <Input
              type='select'
              value={relationship.left_table}
              onChange={(e) => handleRelationshipChange('left_table', e.target.value)}
            >
              <option value=''>Select table...</option>
              {tables.map((table) => (
                <option key={table.name} value={table.name}>
                  {table.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <Label>Right Table</Label>
            <Input
              type='select'
              value={relationship.right_table}
              onChange={(e) => handleRelationshipChange('right_table', e.target.value)}
            >
              <option value=''>Select table...</option>
              {tables.map((table) => (
                <option key={table.name} value={table.name}>
                  {table.name}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* Join Configuration */}
      <Row className='mb-3'>
        <Col lg={6}>
          <FormGroup>
            <Label>Join Type</Label>
            <Input
              type='select'
              value={relationship.join_type}
              onChange={(e) => handleRelationshipChange('join_type', e.target.value)}
            >
              <option value='left_outer'>Left Outer</option>
              <option value='inner'>Inner</option>
            </Input>
          </FormGroup>
        </Col>
        <Col lg={6}>
          <FormGroup>
            <Label>Relationship Type</Label>
            <Input
              type='select'
              value={relationship.relationship_type}
              onChange={(e) => handleRelationshipChange('relationship_type', e.target.value)}
            >
              <option value='many_to_one'>Many to One</option>
              <option value='one_to_one'>One to One</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>

      {/* Column Mappings */}
      <div className='mb-3'>
        <div className='d-flex justify-content-between align-items-center mb-2'>
          <Label>Column Mappings</Label>
          <Button color='primary' outline onClick={addColumnMapping}>
            <i className='fal fa-plus me-1' />
            Add Mapping
          </Button>
        </div>

        {relationship.relationship_columns.map((mapping, index) => (
          <div key={index} className='mb-2 p-3 border rounded bg-light'>
            <Row>
              <Col lg={5}>
                <FormGroup>
                  <Label className='small'>Left Column</Label>
                  <Input
                    type='text'
                    value={mapping.left_column}
                    onChange={(e) => updateColumnMapping(index, 'left_column', e.target.value)}
                    placeholder='Column from left table'
                  />
                </FormGroup>
              </Col>
              <Col lg={5}>
                <FormGroup>
                  <Label className='small'>Right Column</Label>
                  <Input
                    type='text'
                    value={mapping.right_column}
                    onChange={(e) => updateColumnMapping(index, 'right_column', e.target.value)}
                    placeholder='Column from right table'
                  />
                </FormGroup>
              </Col>
              <Col lg={2}>
                <div className='d-flex align-items-end h-100 pb-2'>
                  <Button size='sm' color='danger' outline onClick={() => removeColumnMapping(index)}>
                    <i className='fal fa-trash' />
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        ))}

        {relationship.relationship_columns.length === 0 && (
          <BasicAlert type='info' className='small'>
            No column mappings defined yet.
          </BasicAlert>
        )}
      </div>
    </div>
  );
};
